/**
 * Collection — "Minha Coleção": galeria (somente leitura) de tudo que o jogador abriu.
 * Porte fiel do handoff (Band 3B · "04 · A coleção"): header com stats, filtros e
 * grade de 6 colunas com cartas grandes (chip de posição, badge de status no topo,
 * avatar com anel, nome, "Posição · Comportamento" e barra de status no rodapé).
 *
 * Sem botão "+ Adicionar" (a colagem é no Álbum, via picker do slot vazio).
 * "Repetida" NÃO exibe pontos (§0 da spec).
 */

import React, { useMemo, useState } from 'react';
import { RepositorySticker } from 'services/copa.service';
import { ColleagueFace } from '../ColleagueFace';
import styles from './Collection.module.scss';

type FilterChip = 'todas' | 'titular' | 'craque' | 'lendaria' | 'repetida';

const TOTAL_SLOTS = 15;

// behavior → chip de posição + rótulo (Titular). Craque/Técnico têm chip próprio.
// Mantém os MESMOS chips do StickerCard (Álbum) para consistência entre as telas.
const BEHAVIOR_CHIP: Record<string, string> = {
  foco: 'GOL', colaboracao: 'LAT', inovacao: 'MEI', resolutividade: 'ATA', lideranca: 'TEC',
};
const BEHAVIOR_LABEL: Record<string, string> = {
  foco: 'Foco', colaboracao: 'Colaboração', inovacao: 'Inovação', resolutividade: 'Protagonismo', lideranca: 'Liderança',
};
const POSITION_LABEL: Record<string, string> = {
  foco: 'Goleiro', colaboracao: 'Lateral', inovacao: 'Meio-campo', resolutividade: 'Atacante', lideranca: 'Técnico',
};

interface CollectionProps {
  repository: RepositorySticker[];
  /** Total conquistado (coladas + lendárias) — usado no "X/15 no álbum" */
  conqueredCount?: number;
  /** Clicar numa figurinha abre o detalhe do reconhecimento (RN-30) */
  onViewRecognition?: (recognitionId: string) => void;
}

export function Collection({ repository, conqueredCount, onViewRecognition }: CollectionProps) {
  const [activeFilter, setActiveFilter] = useState<FilterChip>('todas');

  // recognitionIds que são CÓPIAS EXTRAS (repetidas): por colega, todas menos a "primária".
  // Primária = a colada (se houver) ou a 1ª (menor recognitionId). A colada nunca é repetida.
  // Assim "Repetidas" = total − únicas (ex.: 2 Igor + 2 Silvio[1 colado] = 2 repetidas).
  const repeatedSet = useMemo(() => {
    const groups = new Map<string, RepositorySticker[]>();
    for (const s of repository) {
      const arr = groups.get(s.colleagueId);
      if (arr) arr.push(s);
      else groups.set(s.colleagueId, [s]);
    }
    const set = new Set<string>();
    groups.forEach(arr => {
      if (arr.length <= 1) return;
      const collated = arr.find(s => s.collected);
      const primaryId = collated
        ? collated.recognitionId
        : [...arr].sort((a, b) => a.recognitionId.localeCompare(b.recognitionId))[0].recognitionId;
      for (const s of arr) {
        if (!s.collected && s.recognitionId !== primaryId) set.add(s.recognitionId);
      }
    });
    return set;
  }, [repository]);

  // Figurinhas avulsas: ao colar no álbum, a carta SAI da Coleção (vira parte do álbum).
  // A Coleção mostra apenas o que ainda não foi escalado.
  const loose = useMemo(() => repository.filter(s => !s.collected), [repository]);

  const unique = useMemo(() => new Set(loose.map(s => s.colleagueId)).size, [loose]);
  const repeated = repeatedSet.size;
  // "No álbum" = coladas + lendárias conquistadas (conqueredCount). Sem o prop,
  // cai no fallback de figurinhas coladas (lendárias não vivem no repositório).
  const collatedStickers = useMemo(() => repository.filter(s => s.collected).length, [repository]);
  const inAlbum = conqueredCount ?? collatedStickers;

  const counts = useMemo(
    () => ({
      todas: loose.length,
      titular: loose.filter(s => s.type === 'titular').length,
      craque: loose.filter(s => s.type === 'craque' || s.type === 'tecnico').length,
      lendaria: 0,
      repetida: repeated,
    }),
    [loose, repeated],
  );

  const filters: { id: FilterChip; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    { id: 'titular', label: 'Titulares' },
    { id: 'craque', label: 'Craques' },
    { id: 'repetida', label: 'Repetidas' },
  ];

  const filtered = useMemo(() => {
    switch (activeFilter) {
      case 'titular': return loose.filter(s => s.type === 'titular');
      case 'craque': return loose.filter(s => s.type === 'craque' || s.type === 'tecnico');
      case 'repetida': return loose.filter(s => repeatedSet.has(s.recognitionId));
      default: return loose;
    }
  }, [loose, activeFilter, repeatedSet]);

  if (repository.length === 0) {
    return (
      <div className={styles.empty} role="status">
        <p className={styles.emptyText}>
          Sua coleção está vazia. <br />
          Abra um envelope para começar!
        </p>
      </div>
    );
  }

  // Tudo o que foi aberto já está escalado no álbum: coleção sem figurinhas avulsas.
  if (loose.length === 0) {
    return (
      <div className={styles.empty} role="status">
        <p className={styles.emptyText}>
          Todas as suas figurinhas já estão no álbum! <br />
          Abra mais envelopes para colecionar.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerBg} aria-hidden="true" />
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <div className={styles.headerPill} aria-hidden="true">Tudo o que você abriu</div>
            <h2 className={styles.headerTitle}>Minha Coleção</h2>
            <p className={styles.headerSub}>
              {loose.length} figurinha{loose.length === 1 ? '' : 's'} na coleção · cole no álbum pela aba Álbum.
            </p>
          </div>
          <div className={styles.headerStats}>
            <div className={styles.statBox}>
              <div className={styles.statValue} style={{ color: '#7cffeb' }}>{unique}</div>
              <div className={styles.statLabel}>Únicas</div>
            </div>
            <div className={styles.statBox}>
              <div className={styles.statValue} style={{ color: '#fbfd00' }}>{repeated}</div>
              <div className={styles.statLabel}>Repetidas</div>
            </div>
            <div className={styles.statBox}>
              <div className={styles.statValue}>
                {inAlbum}<span style={{ fontSize: 13, color: '#a9bcd6' }}>/{TOTAL_SLOTS}</span>
              </div>
              <div className={styles.statLabel}>No álbum</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className={styles.filterRow} role="group" aria-label="Filtrar coleção">
        {filters.map(f => (
          <button
            key={f.id}
            className={`${styles.filterChip} ${activeFilter === f.id ? styles.filterActive : ''}`}
            onClick={() => setActiveFilter(f.id)}
            aria-pressed={activeFilter === f.id}
            type="button"
          >
            {f.label}{counts[f.id] ? ` · ${counts[f.id]}` : ''}
          </button>
        ))}
      </div>

      {/* ── Grade ── */}
      <div className={styles.grid} role="list" aria-label="Figurinhas na coleção">
        {filtered.map(sticker => {
          const repeatedHere = repeatedSet.has(sticker.recognitionId);
          const isCraque = sticker.type === 'craque' || sticker.type === 'tecnico';
          const chip = isCraque ? (sticker.type === 'tecnico' ? 'TEC' : '★') : (BEHAVIOR_CHIP[sticker.behavior] ?? '—');
          const posLabel = POSITION_LABEL[sticker.behavior] ?? '';
          const behaviorLabel = BEHAVIOR_LABEL[sticker.behavior] ?? sticker.behavior;
          const name = sticker.colleagueName ?? sticker.colleagueId;
          const initial = (name || '?').charAt(0).toUpperCase();

          const variantClass = isCraque ? styles.cardCraque : styles.cardTitular;
          const stateClass = repeatedHere ? styles.cardRepetida : '';

          const handleView = onViewRecognition
            ? () => onViewRecognition(sticker.recognitionId)
            : undefined;

          // Rótulo do leitor de tela: IDENTIDADE primeiro (nome + o que a carta é), depois
          // um estado curto. A Coleção é só leitura, então o rótulo NÃO ensina a mecânica de
          // colar/trocar — isso é assunto da aba Álbum, onde a ação acontece. "Carta de X"
          // deixa claro que posição/comportamento é o TIPO da carta, não uma escalação real
          // (evita a confusão relatada por usuária cega, que ouvia "Nome, Meio-campo" e
          // achava que tinha escalado a pessoa ali). Distingue só os dois tipos de repetida,
          // porque isso muda o que ela consegue fazer com a cópia: travada = colega já está
          // no álbum (RN-28, mesmo `colleagueLocked` que o seletor do Álbum usa).
          const stateSuffix = !repeatedHere
            ? ''
            : sticker.colleagueLocked
            ? ' Pessoa repetida. Você já tem esta pessoa no seu álbum.'
            : ' Pessoa repetida.';

          // Raridade da carta (Titular/Craque/Técnico): visualmente vem da cor da borda +
          // badge, invisível para o leitor de tela — então entra no rótulo. No Técnico a
          // posição também é "Técnico"; evita "Carta Técnico de Técnico".
          const rarityWord =
            sticker.type === 'titular' ? 'Titular' : sticker.type === 'tecnico' ? 'Técnico' : 'Craque';
          const cardTypeDesc =
            posLabel && posLabel !== rarityWord
              ? `Carta ${rarityWord} de ${posLabel}, comportamento ${behaviorLabel}`
              : `Carta ${rarityWord}, comportamento ${behaviorLabel}`;
          const ariaLabel = `${name}. ${cardTypeDesc}.${stateSuffix} Toque para ver o reconhecimento.`;

          // Conteúdo visual — idêntico para carta clicável (<button>) e não-clicável.
          // Tudo aria-hidden: o nome acessível vem do aria-label do container, então os
          // textos internos (nome, badge, posição) não são lidos em duplicidade.
          const cardContent = (
            <>
              {/* chip de posição (topo-esq) */}
              <span className={styles.chip} aria-hidden="true">{chip}</span>

              {/* badge de raridade (topo-dir) — nunca "NO ÁLBUM": a Coleção só mostra o
                  que ainda não foi colado (ver `loose` acima) */}
              <span className={`${styles.badge} ${isCraque ? styles.badgeCraque : styles.badgeTitular}`} aria-hidden="true">
                {isCraque ? 'CRAQUE' : 'TITULAR'}
              </span>

              {/* face: foto → avatar → inicial */}
              <div className={styles.avatarWrap} aria-hidden="true">
                <div className={styles.avatarCircle}>
                  <ColleagueFace
                    image={sticker.image}
                    avatar={sticker.avatar}
                    avatarType={sticker.avatarType}
                    initial={initial}
                    size={56}
                    initialClassName={styles.faceInitial}
                  />
                </div>
              </div>

              {/* nome + posição·comportamento */}
              <div className={styles.cardText} aria-hidden="true">
                <div className={styles.cardName}>{name}</div>
                <div className={styles.cardMeta}>{posLabel} · {behaviorLabel}</div>
              </div>

              {/* footer de status (sem "+ Adicionar"; repetida sem pontos) */}
              <div
                className={`${styles.cardFooter} ${repeatedHere ? styles.footerRepeat : styles.footerColecao}`}
                aria-hidden="true"
              >
                {repeatedHere ? 'Pessoa repetida' : 'Na coleção'}
              </div>
            </>
          );

          const cardClass = `${styles.card} ${variantClass} ${stateClass}`;

          // Wrapper listitem preserva o "item X de N"; a carta interna é um <button> real
          // (semântica de botão + Enter/Espaço nativos) quando abre o reconhecimento.
          return (
            <div key={sticker.recognitionId} role="listitem" className={styles.cardItem}>
              {handleView ? (
                <button type="button" className={`${cardClass} ${styles.clickable}`} aria-label={ariaLabel} onClick={handleView}>
                  {cardContent}
                </button>
              ) : (
                <div className={cardClass} role="img" aria-label={ariaLabel}>
                  {cardContent}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
